import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { gql, useQuery, useMutation } from '@apollo/client';
import {
  PlusIcon,
  TrashIcon,
  UserGroupIcon,
  DocumentArrowUpIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { useLayoutContext } from '~/components/layout/Layout';
import {
  GET_AUDIENCES_BY_AGENT,
  CREATE_AUDIENCE_WITH_CONTACTS,
  DELETE_AUDIENCE,
  ADD_CONTACTS_TO_AUDIENCE,
} from '~/services/audiences';

interface Contact {
  phone_number: string;
  first_name?: string;
  last_name?: string;
  profile_name?: string;
  variables?: { [key: string]: string }; // For template variable mapping
}

interface Audience {
  _id: string;
  name: string;
  description?: string;
  type: 'STATIC' | 'DYNAMIC' | 'IMPORTED';
  estimated_size?: number;
  created_at: string;
  import_metadata?: {
    source: string;
    file_name?: string;
    total_imported: number;
    failed_imports: number;
  };
}

interface CSVData {
  headers: string[];
  rows: string[][];
  fileName: string;
}

interface ColumnMapping {
  [csvColumn: string]:
    | 'phone_number'
    | 'first_name'
    | 'last_name'
    | 'variable'
    | 'ignore';
}

interface AudiencesModuleProps {
  agentId: string;
}

export const AudiencesModule = ({ agentId }: AudiencesModuleProps) => {
  const [t] = useTranslation('campaigns');
  const { renderToast } = useLayoutContext();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<string | null>(null);
  const [newAudienceName, setNewAudienceName] = useState('');
  const [newAudienceDescription, setNewAudienceDescription] = useState('');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);

  // CSV mapping state
  const [csvData, setCsvData] = useState<CSVData | null>(null);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({});
  const [showMappingStep, setShowMappingStep] = useState(false);

  // GraphQL hooks
  const {
    data: audiencesData,
    loading,
    refetch,
  } = useQuery(GET_AUDIENCES_BY_AGENT, {
    variables: {
      data: {
        agent_id: agentId,
        filters: {
          limit: 50,
        },
      },
    },
  });

  const [createAudienceWithContacts] = useMutation(
    CREATE_AUDIENCE_WITH_CONTACTS,
  );
  const [deleteAudience] = useMutation(DELETE_AUDIENCE);

  const audiences: Audience[] = audiencesData?.getAudiencesByAgent?.data || [];

  // Parse CSV into headers and rows
  const parseCSVFile = (csvText: string, fileName: string): CSVData => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) {
      throw new Error(
        'CSV debe contener al menos un encabezado y una fila de datos',
      );
    }

  // Determine delimiter (comma vs semicolon) based on header frequency
  const headerLine = lines[0];
  const commaCount = (headerLine.match(/,/g) || []).length;
  const semicolonCount = (headerLine.match(/;/g) || []).length;
  const delimiter = semicolonCount > commaCount ? ';' : ',';
  // Proper CSV parsing that handles quoted fields and chosen delimiter
  const parseCSVLine = (line: string): string[] => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;
      let i = 0;

      while (i < line.length) {
        const char = line[i];
        const nextChar = line[i + 1];

        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            // Escaped quote
            current += '"';
            i += 2;
          } else {
            // Toggle quote state
            inQuotes = !inQuotes;
            i++;
          }
        } else if (char === delimiter && !inQuotes) {
          // Field separator
          result.push(current.trim());
          current = '';
          i++;
        } else {
          current += char;
          i++;
        }
      }

      // Add the last field
      result.push(current.trim());
      return result;
    };

    const headers = parseCSVLine(lines[0]);
    const rows = lines
      .slice(1)
      .map((line) => parseCSVLine(line))
      .filter((row) => row.some((cell) => cell.length > 0))
      .filter((row) => row.length === headers.length); // Only keep rows with correct column count

    return { headers, rows, fileName };
  };

  // Auto-detect column mappings based on common patterns
  const autoDetectMapping = (headers: string[]): ColumnMapping => {
    const mapping: ColumnMapping = {};

    headers.forEach((header) => {
      const lowerHeader = header.toLowerCase();

      if (
        lowerHeader.includes('phone') ||
        lowerHeader.includes('telefono') ||
        lowerHeader.includes('celular')
      ) {
        mapping[header] = 'phone_number';
      } else if (
        lowerHeader.includes('first') ||
        (lowerHeader.includes('nombre') && !lowerHeader.includes('last'))
      ) {
        mapping[header] = 'first_name';
      } else if (
        lowerHeader.includes('last') ||
        lowerHeader.includes('apellido')
      ) {
        mapping[header] = 'last_name';
      } else {
        mapping[header] = 'variable';
      }
    });

    return mapping;
  };

  // Convert mapped CSV data to contacts
  const processCSVWithMapping = (): Contact[] => {
    if (!csvData) return [];

    const processedContacts: Contact[] = [];

    csvData.rows.forEach((row) => {
      // Skip rows that don't match expected column count (already filtered in parseCSVFile)
      if (row.length !== csvData.headers.length) return;

      const contact: Contact = {
        phone_number: '',
        variables: {},
      };

      let hasPhone = false;

      csvData.headers.forEach((header, index) => {
        const value = row[index]?.trim();
        if (!value) return;

        const mapping = columnMapping[header];

        // Always save the raw data in variables with original column name
        if (contact.variables) {
          contact.variables[header] = value;
        }

        switch (mapping) {
          case 'phone_number':
            // Better phone number cleaning - remove decimals and clean formatting
            let cleanPhone = value.replace(/[^\d+]/g, '');
            // Remove trailing zero if it looks like a decimal (e.g., "573192216451.0" -> "573192216451")
            if (cleanPhone.endsWith('0') && value.includes('.')) {
              cleanPhone = cleanPhone.slice(0, -1);
            }
            contact.phone_number = cleanPhone;
            hasPhone =
              !!contact.phone_number && contact.phone_number.length >= 10;
            break;
          case 'first_name':
            contact.first_name = value;
            break;
          case 'last_name':
            contact.last_name = value;
            break;
          case 'variable':
            // Already handled above - saving all data as variables
            break;
          // 'ignore' case - still save in variables but don't map to specific field
        }
      });

      // Only add contacts with valid phone numbers
      if (hasPhone) {
        // Set profile_name if not set
        if (
          !contact.profile_name &&
          (contact.first_name || contact.last_name)
        ) {
          contact.profile_name = [contact.first_name, contact.last_name]
            .filter(Boolean)
            .join(' ');
        }
        processedContacts.push(contact);
      }
    });

    return processedContacts;
  };

  // Handle CSV file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setCsvError('Por favor selecciona un archivo CSV válido');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target?.result as string;
        const data = parseCSVFile(csvText, file.name);
        setCsvData(data);

        // Auto-detect mappings
        const mapping = autoDetectMapping(data.headers);
        setColumnMapping(mapping);

        // Show mapping step
        setShowMappingStep(true);
        setCsvError(null);
      } catch (error) {
        setCsvError(
          error instanceof Error
            ? error.message
            : 'Error procesando archivo CSV',
        );
      }
    };
    reader.readAsText(file);
  };

  // Apply CSV mapping and add contacts
  const applyCSVMapping = () => {
    // Validate that at least one column is mapped to phone_number
    const phoneMapping = Object.values(columnMapping).some(
      (mapping) => mapping === 'phone_number',
    );
    if (!phoneMapping) {
      setCsvError('Debes mapear al menos una columna como "Teléfono"');
      return;
    }

    try {
      const newContacts = processCSVWithMapping();
      setContacts((prev) => [...prev, ...newContacts]);
      setShowMappingStep(false);
      setCsvData(null);
      setColumnMapping({});
      setCsvError(null);

  // toast removed per request
    } catch (error) {
      setCsvError(
        error instanceof Error ? error.message : 'Error procesando contactos',
      );
    }
  };

  // Reset dialog state
  const resetCreateDialog = () => {
    setShowCreateDialog(false);
    setNewAudienceName('');
    setNewAudienceDescription('');
    setContacts([]);
    setCsvData(null);
    setColumnMapping({});
    setShowMappingStep(false);
    setCsvError(null);
  };

  const addManualContact = () => {
    setContacts([
      ...contacts,
      {
        phone_number: '',
        first_name: '',
        last_name: '',
        variables: {},
      },
    ]);
  };

  const updateContact = (
    index: number,
    field: keyof Contact,
    value: string,
  ) => {
    const updated = [...contacts];
    if (field === 'variables') return; // Handle variables separately
    updated[index] = { ...updated[index], [field]: value };
    setContacts(updated);
  };

  const addVariable = (contactIndex: number, key: string, value: string) => {
    const updated = [...contacts];
    updated[contactIndex].variables = {
      ...updated[contactIndex].variables,
      [key]: value,
    };
    setContacts(updated);
  };

  const removeContact = (index: number) => {
    setContacts(contacts.filter((_, i) => i !== index));
  };

  const handleCreateAudience = async () => {
    if (!newAudienceName.trim() || contacts.length === 0) return;

    setIsCreating(true);
    try {
      const { data } = await createAudienceWithContacts({
        variables: {
          data: {
            agent_id: agentId,
            name: newAudienceName.trim(),
            description: newAudienceDescription.trim() || undefined,
            contacts: contacts.map((contact) => ({
              phone_number: contact.phone_number,
              first_name: contact.first_name || undefined,
              last_name: contact.last_name || undefined,
              profile_name: contact.profile_name || undefined,
              variables: contact.variables || {},
            })),
          },
        },
      });

      if (data?.createAudienceWithContacts?.hasError) {
        throw new Error(
          data.createAudienceWithContacts.message || 'Error creando audiencia',
        );
      }

      renderToast({
        type: 'success',
        message: 'Audiencia creada exitosamente',
      });

      resetCreateDialog();
      refetch();
    } catch (error) {
      console.error('Error creating audience:', error);
      renderToast({
        type: 'error',
        message:
          error instanceof Error ? error.message : 'Error creando audiencia',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteAudience = async (audienceId: string) => {
    setIsDeleting(true);
    try {
      const { data } = await deleteAudience({
        variables: { audience_id: audienceId },
      });

      if (data?.deleteAudience?.hasError) {
        throw new Error(
          data.deleteAudience.message || 'Error eliminando audiencia',
        );
      }

      renderToast({
        type: 'success',
        message: 'Audiencia eliminada exitosamente',
      });

      setShowDeleteDialog(null);
      refetch();
    } catch (error) {
      console.error('Error deleting audience:', error);
      renderToast({
        type: 'error',
        message:
          error instanceof Error ? error.message : 'Error eliminando audiencia',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Audiencias</h2>
          <p className="mt-1 text-sm text-gray-600">
            Organiza tus contactos en audiencias para tus campañas
          </p>
        </div>
        <button
          onClick={() => setShowCreateDialog(true)}
          className="btn btn-primary rounded-full"
        >
          <PlusIcon className="mr-2 h-4 w-4" />
          Nueva Audiencia
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="loading loading-spinner loading-lg"></div>
        </div>
      )}

      {/* Audiences List */}
      {audiences.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {audiences.map((audience) => (
            <div
              key={audience._id}
              className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <UserGroupIcon className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-sm font-medium text-gray-900">
                      {audience.name}
                    </h3>
                    {audience.description && (
                      <p className="mt-1 text-xs text-gray-600">
                        {audience.description}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowDeleteDialog(audience._id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-3 space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Contactos:</span>
                  <span className="font-medium">
                    {audience.estimated_size || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Tipo:</span>
                  <span
                    className={`rounded-full px-2 py-1 text-xs font-medium ${
                      audience.type === 'IMPORTED'
                        ? 'bg-green-100 text-green-800'
                        : audience.type === 'STATIC'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-purple-100 text-purple-800'
                    }`}
                  >
                    {audience.type === 'IMPORTED'
                      ? 'Importada'
                      : audience.type === 'STATIC'
                        ? 'Estática'
                        : 'Dinámica'}
                  </span>
                </div>
                {audience.import_metadata?.file_name && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Archivo:</span>
                    <span className="max-w-20 truncate">
                      {audience.import_metadata.file_name}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex h-64 flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 text-center">
          <UserGroupIcon className="mb-4 h-12 w-12 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900">
            No hay audiencias creadas
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            Crea tu primera audiencia para organizar tus contactos
          </p>
          <button
            onClick={() => setShowCreateDialog(true)}
            className="btn btn-primary btn-sm mt-4 rounded-full"
          >
            <PlusIcon className="mr-1 h-4 w-4" />
            Crear Primera Audiencia
          </button>
        </div>
      )}

      {/* Create Audience Dialog - DaisyUI Style */}
      <dialog
        id="create_audience_modal"
        className={`modal ${showCreateDialog ? 'modal-open' : ''}`}
      >
        <div className="modal-box max-h-[90vh] max-w-4xl overflow-y-auto">
          <form method="dialog">
            <button
              className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
              onClick={resetCreateDialog}
            >
              ✕
            </button>
          </form>

          <h3 className="mb-4 text-lg font-bold">
            {showMappingStep ? 'Mapear Columnas CSV' : 'Crear Nueva Audiencia'}
          </h3>

          {!showMappingStep ? (
            /* Main Create Form */
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <label className="label">
                    <span className="label-text">Nombre de la Audiencia *</span>
                  </label>
                  <input
                    type="text"
                    value={newAudienceName}
                    onChange={(e) => setNewAudienceName(e.target.value)}
                    placeholder="Ej: Clientes Premium"
                    className="input input-bordered w-full"
                  />
                </div>
                <div>
                  <label className="label">
                    <span className="label-text">Descripción (Opcional)</span>
                  </label>
                  <input
                    type="text"
                    value={newAudienceDescription}
                    onChange={(e) => setNewAudienceDescription(e.target.value)}
                    placeholder="Ej: Clientes con compras >$100"
                    className="input input-bordered w-full"
                  />
                </div>
              </div>

              {/* CSV Upload */}
              <div className="form-control">
                <div className="mb-2 flex items-center justify-between">
                  <label className="label">
                    <span className="label-text">Importar Contactos</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="btn btn-outline btn-sm"
                  >
                    <DocumentArrowUpIcon className="mr-1 h-4 w-4" />
                    Subir CSV
                  </button>
                </div>
                <div className="mb-3 text-xs text-gray-600">
                  Selecciona un archivo CSV y podrás mapear las columnas a los
                  campos necesarios.
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleFileUpload}
                />
                {csvError && (
                  <div className="alert alert-error mt-2">
                    <span>{csvError}</span>
                  </div>
                )}
              </div>

              {/* Manual Contact Addition */}
              <div className="form-control">
                <div className="mb-3 flex items-center justify-between">
                  <label className="label">
                    <span className="label-text">
                      Contactos ({contacts.length})
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={addManualContact}
                    className="btn btn-outline btn-sm"
                  >
                    <PlusIcon className="mr-1 h-4 w-4" />
                    Agregar Manual
                  </button>
                </div>

                {contacts.length > 0 && (
                  <div className="overflow-x-auto rounded-lg border">
                    <table className="table-sm table">
                      <thead>
                        <tr>
                          <th>Teléfono *</th>
                          <th>Nombre</th>
                          <th>Apellido</th>
                          <th>Variables</th>
                          <th>Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contacts.map((contact, index) => (
                          <tr key={index}>
                            <td>
                              <input
                                type="tel"
                                value={contact.phone_number}
                                onChange={(e) =>
                                  updateContact(
                                    index,
                                    'phone_number',
                                    e.target.value,
                                  )
                                }
                                placeholder="573001234567"
                                className="input input-bordered input-xs w-full"
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                value={contact.first_name || ''}
                                onChange={(e) =>
                                  updateContact(
                                    index,
                                    'first_name',
                                    e.target.value,
                                  )
                                }
                                placeholder="Juan"
                                className="input input-bordered input-xs w-full"
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                value={contact.last_name || ''}
                                onChange={(e) =>
                                  updateContact(
                                    index,
                                    'last_name',
                                    e.target.value,
                                  )
                                }
                                placeholder="Pérez"
                                className="input input-bordered input-xs w-full"
                              />
                            </td>
                            <td>
                              <div className="text-xs">
                                {Object.keys(contact.variables || {}).length > 0 ? (
                                  <details className="dropdown">
                                    <summary className="btn btn-ghost btn-xs text-gray-500 cursor-pointer">
                                      {Object.keys(contact.variables || {}).length} vars
                                    </summary>
                                    <div className="dropdown-content z-[1] p-2 shadow bg-base-100 rounded-box w-52 max-h-32 overflow-y-auto">
                                      {Object.entries(contact.variables || {}).map(([key, value]) => (
                                        <div key={key} className="text-xs mb-1">
                                          <span className="font-medium">{key}:</span> {value}
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                ) : (
                                  <span className="text-gray-400">0 vars</span>
                                )}
                              </div>
                            </td>
                            <td>
                              <button
                                type="button"
                                onClick={() => removeContact(index)}
                                className="btn btn-ghost btn-xs text-error"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {contacts.length === 0 && (
                  <div className="rounded-lg border border-dashed border-gray-300 py-8 text-center">
                    <p className="text-sm text-gray-500">
                      No hay contactos. Sube un CSV o agrega contactos
                      manualmente.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* CSV Column Mapping Step */
            <div className="space-y-6">
              <div className="alert alert-info">
                <span>
                  Mapea las columnas de tu archivo CSV ({csvData?.fileName}) a
                  los campos necesarios. Al menos una columna debe ser mapeada
                  como "Teléfono".
                </span>
              </div>
              <div className="text-sm text-gray-600 mb-2">
                Se cargarán {csvData?.rows.length || 0} registros con este
                esquema.
              </div>

              <div className="overflow-x-auto">
                <table className="table-zebra table">
                  <thead>
                    <tr>
                      <th>Columna CSV</th>
                      <th>
                        Vista Previa  
                      </th>
                      <th>Mapear a</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvData?.headers.map((header, index) => (
                      <tr key={header}>
                        <td className="font-medium">{header}</td>
                        <td className="text-sm text-gray-600">
                          {csvData.rows[0]?.[index] || 'N/A'}
                        </td>
                        <td>
                          <select
                            value={columnMapping[header] || 'ignore'}
                            onChange={(e) =>
                              setColumnMapping((prev) => ({
                                ...prev,
                                [header]: e.target.value as any,
                              }))
                            }
                            className="select select-bordered select-sm w-full max-w-xs"
                          >
                            <option value="ignore">Ignorar</option>
                            <option value="phone_number">Teléfono</option>
                            <option value="first_name">Nombre</option>
                            <option value="last_name">Apellido</option>
                            <option value="variable">
                              Variable Personalizada
                            </option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {csvError && (
                <div className="alert alert-error">
                  <span>{csvError}</span>
                </div>
              )}

              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={() => setShowMappingStep(false)}
                  className="btn btn-ghost"
                >
                  <ArrowLeftIcon className="mr-2 h-4 w-4" />
                  Volver
                </button>
                <button
                  type="button"
                  onClick={applyCSVMapping}
                  className="btn btn-primary"
                >
                  Aplicar Mapeo
                  <ArrowRightIcon className="ml-2 h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {!showMappingStep && (
            <div className="modal-action">
              <button
                type="button"
                onClick={resetCreateDialog}
                className="btn btn-ghost"
                disabled={isCreating}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCreateAudience}
                className="btn btn-primary"
                disabled={
                  isCreating || !newAudienceName.trim() || contacts.length === 0
                }
              >
                {isCreating ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Creando...
                  </>
                ) : (
                  'Crear Audiencia'
                )}
              </button>
            </div>
          )}
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={resetCreateDialog}>close</button>
        </form>
      </dialog>

      {/* Delete Confirmation Dialog - DaisyUI Style */}
      <dialog
        id="delete_audience_modal"
        className={`modal ${showDeleteDialog ? 'modal-open' : ''}`}
      >
        <div className="modal-box">
          <form method="dialog">
            <button
              className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
              onClick={() => setShowDeleteDialog(null)}
            >
              ✕
            </button>
          </form>

          <h3 className="text-lg font-bold">Confirmar Eliminación</h3>
          <p className="py-4">
            ¿Estás seguro de que quieres eliminar esta audiencia? Esta acción no
            se puede deshacer.
          </p>

          <div className="modal-action">
            <button
              onClick={() => setShowDeleteDialog(null)}
              className="btn btn-ghost"
              disabled={isDeleting}
            >
              Cancelar
            </button>
            <button
              onClick={() =>
                showDeleteDialog && handleDeleteAudience(showDeleteDialog)
              }
              className="btn btn-error"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Eliminando...
                </>
              ) : (
                'Eliminar'
              )}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setShowDeleteDialog(null)}>close</button>
        </form>
      </dialog>
    </div>
  );
};
